import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge } from '../components/primitives';
import { LocationEditor, ContactEditor, ContractEditor } from './clientEditors';
import { st } from '../lib/status';
import { initials } from '../lib/avatar';
import { can } from '../lib/rbac';
import type { Client, ClientLocation, Contact, Contract, Project } from '../../shared/types';

export function ClientDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const nav = useNavigate();
  const { openDrawer, refresh, user } = useApp();
  const { data: client, error } = useFetch<Client>(`/clients/${id}`);
  const { data: locations } = useFetch<ClientLocation[]>(`/clients/${id}/locations`);
  const { data: contacts } = useFetch<Contact[]>(`/clients/${id}/contacts`);
  const { data: allProjects } = useFetch<Project[]>('/projects');
  const { data: contractList } = useFetch<Contract[]>(`/clients/${id}/contracts`);
  const [editLoc, setEditLoc] = useState<ClientLocation | 'new' | null>(null);
  const [editContact, setEditContact] = useState<Contact | 'new' | null>(null);
  const [editContract, setEditContract] = useState<Contract | 'new' | null>(null);

  const canEdit = !!user && can(user.role, 'update', 'clients');
  const locs = locations || [];
  const contracts = contractList || [];
  const projects = (allProjects || []).filter((p) => p.client_id === id);
  const locLabel = (lid: number | null) => (lid ? locs.find((l) => l.id === lid)?.label || 'Location' : 'Client-level');
  const fullAddr = (l: ClientLocation) => [l.addr_line1, l.addr_line2, [l.city, l.state].filter(Boolean).join(', '), l.zip].filter(Boolean).join(' · ');

  if (error) return <BackShell nav={nav}><Empty>Couldn’t load this client.</Empty></BackShell>;
  if (!client) return <BackShell nav={nav}><Empty>Loading…</Empty></BackShell>;

  return (
    <Fade>
      <BackLink nav={nav} />
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="cc-mono" style={{ width: 52, height: 52, flex: '0 0 auto', borderRadius: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: 'var(--text-2)' }}>{initials(client.name)}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>{client.name}</span>
              <StatusBadge badge={st(client.status)} />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{client.industry} · client since {client.since}</div>
          </div>
          {canEdit && <Button variant="secondary" icon="edit" onClick={() => openDrawer({ type: 'client', mode: 'edit', id })}>Edit</Button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, padding: '16px 20px' }}>
          {metaCell('Industry', client.industry || '—')}
          {metaCell('Active projects', client.projects_label)}
          {metaCell('Primary contact', client.primary_contact || '—')}
          {metaCell('Locations', String(locs.length))}
        </div>
      </Card>

      <Section
        title="Locations"
        action={canEdit ? <Button variant="ghost" icon="add" onClick={() => setEditLoc('new')}>Add location</Button> : null}
      >
        {locs.length === 0 && <Empty>No locations yet.</Empty>}
        {locs.map((l) => (
          <Row key={l.id} onClick={canEdit ? () => setEditLoc(l) : undefined} canEdit={canEdit}>
            <Icon name={l.is_hq ? 'corporate_fare' : 'location_on'} size={20} color="var(--text-3)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{l.label}</span>
                {l.is_hq && <Pill>HQ</Pill>}
                {l.lat == null && <span title="ZIP not recognized — won't rank in assignments" style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--warning)' }}>No geo</span>}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{fullAddr(l) || '—'}</div>
            </div>
            {canEdit && <Icon name="chevron_right" size={18} color="var(--text-3)" />}
          </Row>
        ))}
      </Section>

      <Section
        title="Points of contact"
        action={canEdit ? <Button variant="ghost" icon="add" onClick={() => setEditContact('new')}>Add contact</Button> : null}
      >
        {(contacts || []).length === 0 && <Empty>No contacts yet.</Empty>}
        {(contacts || []).map((c) => (
          <Row key={c.id} onClick={canEdit ? () => setEditContact(c) : undefined} canEdit={canEdit}>
            <Avatar initials={initials(c.name)} bg="var(--surface-2)" fg="var(--text-2)" size={34} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{c.name}</span>
                {c.primary && <Pill>Primary</Pill>}
                <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 5 }}>{locLabel(c.location_id)}</span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{[c.title, c.email, c.phone].filter(Boolean).join(' · ') || '—'}</div>
            </div>
            {canEdit && <Icon name="chevron_right" size={18} color="var(--text-3)" />}
          </Row>
        ))}
      </Section>

      <Section
        title={`Contracts & POs (${contracts.length})`}
        action={canEdit ? <Button variant="ghost" icon="add" onClick={() => setEditContract('new')}>Add contract</Button> : null}
      >
        {contracts.length === 0 && <Empty>No contracts yet.</Empty>}
        {contracts.map((k) => (
          <Row key={k.id} onClick={canEdit ? () => setEditContract(k) : undefined} canEdit={canEdit}>
            <Icon name="description" size={20} color="var(--text-3)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{k.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{[k.reference, k.agency, [k.start_date, k.end_date].filter(Boolean).join(' → ')].filter(Boolean).join(' · ') || '—'}</div>
            </div>
            {canEdit && <Icon name="chevron_right" size={18} color="var(--text-3)" />}
          </Row>
        ))}
      </Section>

      <Section title={`Projects (${projects.length})`}>
        {projects.length === 0 && <Empty>No projects for this client.</Empty>}
        {projects.map((p) => (
          <Row key={p.id} onClick={() => openDrawer({ type: 'project', mode: 'edit', id: p.id })} canEdit>
            <Icon name="view_kanban" size={20} color="var(--text-3)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{p.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{p.priority} priority{p.due_date ? ` · due ${p.due_date}` : ''}</div>
            </div>
            <StatusBadge badge={st(p.status)} />
          </Row>
        ))}
      </Section>

      {editLoc && <LocationEditor clientId={id} location={editLoc === 'new' ? null : editLoc} onClose={() => setEditLoc(null)} onSaved={() => { refresh(); setEditLoc(null); }} />}
      {editContact && <ContactEditor clientId={id} contact={editContact === 'new' ? null : editContact} locations={locs} onClose={() => setEditContact(null)} onSaved={() => { refresh(); setEditContact(null); }} />}
      {editContract && <ContractEditor clientId={id} contract={editContract === 'new' ? null : editContract} onClose={() => setEditContract(null)} onSaved={() => { refresh(); setEditContract(null); }} />}
    </Fade>
  );
}

function BackLink({ nav }: { nav: ReturnType<typeof useNavigate> }) {
  return (
    <button onClick={() => nav('/clients')} className="cc-hover-s2 cc-clickable" style={{ appearance: 'none', border: 0, background: 'transparent', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--text-3)', padding: '4px 8px 4px 4px', borderRadius: 7, marginBottom: 12 }}>
      <Icon name="arrow_back" size={18} />Clients
    </button>
  );
}

function BackShell({ nav, children }: { nav: ReturnType<typeof useNavigate>; children: ReactNode }) {
  return <Fade><BackLink nav={nav} />{children}</Fade>;
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        <div style={{ flex: 1 }} />
        {action}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

function Row({ children, onClick, canEdit }: { children: ReactNode; onClick?: () => void; canEdit?: boolean }) {
  return (
    <div onClick={onClick} className={canEdit && onClick ? 'cc-hover-border cc-clickable' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: 'var(--surface)' }}>
      {children}
    </div>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-bg)', padding: '1px 6px', borderRadius: 5 }}>{children}</span>;
}

function Empty({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '4px 2px' }}>{children}</div>;
}

function metaCell(label: string, value: ReactNode) {
  return (
    <div key={label}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
    </div>
  );
}
